import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { AppIcon } from '@capacitor-community/app-icon';
import { Capacitor } from '@capacitor/core';
import { AlertController, LoadingController, Platform } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';

import { isEmpty, isNull } from 'lodash-es';
import { from, map, of, switchMap, withLatestFrom } from 'rxjs';
import { catchError, concatMap, filter, finalize } from 'rxjs/operators';
import { v4 } from 'uuid';

import { isBlackKnight } from '../../helpers/data-provider';
import { AdminPortalApiService } from '../../misc/services/adminPortalApi.service';
import { LoadingService } from '../../misc/services/loading.service';
import { LoggerService } from '../../misc/services/logger.service';
import { StorageService } from '../../misc/services/storage.service';
import { CountyGetResponse } from '../../typings/county';
import { SystemConfigurationsMobileSettingsResponse } from '../../typings/system-configurations';
import * as fromDataProviderActions from '../actions/data-provider.actions';
import * as fromLoggerActions from '../actions/logger.actions';
import * as fromPersistedStateActions from '../actions/persisted-state.actions';
import * as fromSettingActions from '../actions/setting.actions';
import * as fromUserActions from '../actions/user.actions';
import * as fromOrganizationSelectors from '../selectors/organization.selectors';
import * as fromSettingSelectors from '../selectors/setting.selectors';

@Injectable()
export class SettingEffects {
  private actions$ = inject(Actions);
  private adminPortalApiService = inject(AdminPortalApiService);
  private alertController = inject(AlertController);
  private loadingController = inject(LoadingController);
  private loggerService = inject(LoggerService);
  private loadingService = inject(LoadingService);
  private platform = inject(Platform);
  private router = inject(Router);
  private storageService = inject(StorageService);
  private store = inject(Store);

  migratePersistedOldStateReferralCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPersistedStateActions.migratePersistedOldStateReferralCode),
      filter(
        ({
          payload: {
            authData: { repCode }
          }
        }) => !isEmpty(repCode)
      ),
      concatMap(({ payload }) =>
        from(
          this.loadingController.create({
            backdropDismiss: false,
            message: 'Migrating referral code...',
            showBackdrop: true,
            translucent: true
          })
        ).pipe(
          concatMap(async (loader) => {
            await loader.present();
            return loader;
          }),
          switchMap((loader) =>
            this.adminPortalApiService
              .sendRequest('GET', `/v2/system-configurations/mobile/${payload.authData.repCode}`)
              .pipe(
                concatMap(async (data: SystemConfigurationsMobileSettingsResponse) =>
                  data.validCode
                    ? fromPersistedStateActions.migratePersistedOldStateReferralCodeSuccess({
                        payload: {
                          code: payload.authData.repCode,
                          data,
                          state: payload
                        }
                      })
                    : fromPersistedStateActions.migratePersistedOldStateFailure({
                        payload: {
                          error: new Error('Migration failed. The referral code is invalid.')
                        }
                      })
                ),
                catchError(() =>
                  of(
                    fromPersistedStateActions.migratePersistedOldStateFailure({
                      payload: {
                        error: new Error('Unable to validate and migrate referral code.')
                      }
                    })
                  )
                ),
                finalize(async () => {
                  await loader.dismiss();
                })
              )
          )
        )
      )
    )
  );

  migratePersistedOldStateDataProviderCredentialsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPersistedStateActions.migratePersistedOldStateDataProviderCredentialsSuccess),
      map(({ payload: { state } }) =>
        fromPersistedStateActions.migratePersistedOldStateAlternateIconSuccess({
          payload: {
            state,
            useDefault: state.appInfo.currentAppIcon === 'default'
          }
        })
      )
    )
  );

  migratePersistedOldStateAlternateIconSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPersistedStateActions.migratePersistedOldStateAlternateIconSuccess),
      filter(() => Capacitor.isNativePlatform() && this.platform.is('ios')),
      map(({ payload: { useDefault } }) =>
        fromSettingActions.updateApplicationIcon({
          payload: {
            useDefault
          }
        })
      )
    )
  );

  redirectHome$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromPersistedStateActions.migratePersistedOldStateAlternateIconSuccess),
        concatMap(async () => await this.router.navigate(['/']))
      ),
    {
      dispatch: false
    }
  );

  cleanupOldState$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          fromPersistedStateActions.migratePersistedOldStateAlternateIconSuccess,
          fromPersistedStateActions.migratePersistedOldStateFailure
        ),
        concatMap(async () => await this.storageService.removeOldState())
      ),
    {
      dispatch: false
    }
  );

  loadSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromSettingActions.loadSettings),
      filter(({ payload: { referralCode } }) => !isEmpty(referralCode)),
      switchMap(({ payload: { isOnboardFlow, referralCode } }) =>
        this.adminPortalApiService.sendRequest('GET', `/v2/system-configurations/mobile/${referralCode}`).pipe(
          switchMap((data: SystemConfigurationsMobileSettingsResponse) => {
            const actions: Action[] = [];
            if (data.validCode) {
              actions.push(fromSettingActions.loadSettingsSuccess({ payload: { data, isOnboardFlow } }));
              if (data.referralCode) {
                actions.push(fromUserActions.loadEndUserDetails({ payload: { referralCode } }));
              }
            } else {
              actions.push(
                fromSettingActions.loadSettingsFailure({
                  payload: {
                    error: new Error('The referral code is invalid.')
                  }
                })
              );
            }
            return actions;
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              fromSettingActions.loadSettingsFailure({
                payload: {
                  error: new Error(
                    `We are sorry, but there was a issue trying to reach our services (${error.message}).`
                  )
                }
              })
            )
          )
        )
      )
    )
  );

  loadSettingsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromSettingActions.loadSettingsSuccess),
        filter(({ payload: { isOnboardFlow } }) => !!isOnboardFlow),
        concatMap(() => this.router.navigate(['onboarding', 'terms']))
      ),
    {
      dispatch: false
    }
  );

  updateApplicationIcon$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromSettingActions.updateApplicationIcon),
      withLatestFrom(this.store.pipe(select(fromOrganizationSelectors.selectAlternateAppIconName))),
      concatMap(
        async ([
          {
            payload: { useDefault }
          },
          alternateAppIconName
        ]) => {
          if (Capacitor.isNativePlatform() && this.platform.is('ios')) {
            let isSupported = false;
            try {
              const { value } = await AppIcon.isSupported();
              isSupported = value;
            } catch {
              this.loggerService.handleError(new Error('Capacitor App Icon plugin failure (isSupported)'));
            }

            if (isSupported && alternateAppIconName) {
              if (useDefault) {
                let isAlternateIconUsed = false;
                try {
                  isAlternateIconUsed = !isNull(await AppIcon.getName());
                } catch {
                  this.loggerService.handleError(new Error('Capacitor App Icon plugin failure (getName)'));
                }
                if (isAlternateIconUsed) {
                  try {
                    await AppIcon.reset({});
                    return fromSettingActions.updateApplicationIconSuccess({
                      payload: { alternateAppIconUsed: false }
                    });
                  } catch {
                    this.loggerService.handleError(new Error('Capacitor App Icon plugin failure (reset)'));
                    return fromSettingActions.updateApplicationIconFailure({
                      payload: { error: new Error('We encountered a issue trying to change your app icon') }
                    });
                  }
                } else {
                  return fromSettingActions.updateApplicationIconSuccess({
                    payload: { alternateAppIconUsed: false }
                  });
                }
              } else {
                try {
                  await AppIcon.change({ name: alternateAppIconName.replace('-', ' ') });
                  return fromSettingActions.updateApplicationIconSuccess({ payload: { alternateAppIconUsed: true } });
                } catch {
                  this.loggerService.handleError(new Error('Capacitor App Icon plugin failure (change)'));
                  return fromSettingActions.updateApplicationIconFailure({
                    payload: { error: new Error('We encountered a issue trying to change your app icon') }
                  });
                }
              }
            } else if (alternateAppIconName) {
              return fromSettingActions.updateApplicationIconFailure({
                payload: { error: new Error('Changing the icon is not supported on the device') }
              });
            } else {
              return fromSettingActions.updateApplicationIconFailure({
                payload: { error: new Error('No alternate icon is available') }
              });
            }
          } else {
            return fromSettingActions.updateApplicationIconFailure({
              payload: { error: new Error('Changing the icon works only on ios mobile device') }
            });
          }
        }
      )
    )
  );

  updateLoginAccountReferralCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromSettingActions.updateReferralCode),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectApplicationKey))),
      switchMap(
        ([
          {
            payload: { code, password, username }
          },
          applicationKey
        ]) =>
          this.adminPortalApiService.sendRequest('GET', `/v2/system-configurations/mobile/${code}`).pipe(
            concatMap((data: SystemConfigurationsMobileSettingsResponse) => {
              let results: Action[];
              if (data.validCode && data.parse.appKey === applicationKey) {
                results = [
                  fromLoggerActions.updateConfiguration({
                    payload: {
                      appName: data.organization.applicationName,
                      securityCode: code
                    }
                  })
                ];
                if (isBlackKnight(data.provider.name)) {
                  results.push(
                    fromDataProviderActions.updateDataProviderAuthentication({
                      payload: {
                        code,
                        data,
                        password,
                        username
                      }
                    })
                  );
                } else {
                  results.push(
                    fromSettingActions.updateReferralCodeSuccess({
                      payload: { code, data }
                    })
                  );
                }
              } else if (data.parse.appKey !== applicationKey) {
                results = [
                  fromSettingActions.updateReferralCodeFailure({
                    payload: {
                      error: new Error(
                        'Due to some administrative changes, you need to do the onboarding process again.'
                      )
                    }
                  }),
                  fromSettingActions.resetApplication()
                ];
              } else {
                results = [
                  fromSettingActions.updateReferralCodeFailure({
                    payload: {
                      error: new Error('The referral code is invalid.')
                    }
                  })
                ];
              }
              return results;
            }),
            catchError((error: HttpErrorResponse) =>
              of(
                fromSettingActions.updateReferralCodeFailure({
                  payload: {
                    error: new Error(
                      `We are sorry, but there was a issue trying to reach our services (${error.message}).`
                    )
                  }
                })
              )
            )
          )
      )
    )
  );

  refreshReferralCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromSettingActions.refreshReferralCode, fromPersistedStateActions.loadPersistedStateSuccess),
      withLatestFrom(
        this.store.pipe(select(fromSettingSelectors.selectReferralCode)),
        this.store.pipe(select(fromSettingSelectors.selectApplicationKey))
      ),
      map((data) => ({
        applicationKey: data[2],
        code: data[1]
      })),
      filter(({ applicationKey, code }) => !isEmpty(code) && !isEmpty(applicationKey)),
      switchMap(({ applicationKey, code }) =>
        this.adminPortalApiService.sendRequest('GET', `/v2/system-configurations/mobile/${code}`).pipe(
          concatMap((data: SystemConfigurationsMobileSettingsResponse) => {
            let results: Action[];
            if (data.validCode && data.parse.appKey === applicationKey) {
              results = [
                fromLoggerActions.updateConfiguration({
                  payload: {
                    appName: data.organization.applicationName,
                    securityCode: code ?? undefined
                  }
                }),
                fromSettingActions.refreshReferralCodeSuccess({
                  payload: { data }
                })
              ];
            } else if (data.parse.appKey !== applicationKey) {
              results = [
                fromSettingActions.refreshReferralCodeFailure({
                  payload: {
                    error: new Error('Due to some administrative changes, you need to do the onboarding process again.')
                  }
                }),
                fromSettingActions.resetApplication()
              ];
            } else {
              results = [
                fromSettingActions.refreshReferralCodeFailure({
                  payload: {
                    error: new Error('It seems that your referral code is invalid.')
                  }
                })
              ];
            }
            return results;
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              fromSettingActions.refreshReferralCodeFailure({
                payload: {
                  error: new Error(
                    `We are sorry, but there was a issue trying to reach our services (${error.message}).`
                  )
                }
              })
            )
          )
        )
      )
    )
  );

  updateSelectedState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromSettingActions.updateSelectedState),
      filter(({ payload: { state } }) => !isNull(state)),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectParseType))),
      switchMap(
        ([
          {
            payload: { state }
          },
          parseType
        ]) =>
          this.adminPortalApiService
            .sendRequest('GET', `/v2/locations/state/${state}/county`, {
              params: this.adminPortalApiService.getQueryParams({
                parseType: parseType
              })
            })
            .pipe(
              map((payload: CountyGetResponse) => {
                return fromSettingActions.updateAvailableCountiesSuccess({
                  payload
                });
              }),
              catchError((error: HttpErrorResponse) =>
                of(
                  fromSettingActions.updateAvailableCountiesFailure({
                    payload: {
                      error: new Error(
                        `We are sorry, but there was a issue trying to reach our services (${error.message}).`
                      )
                    }
                  })
                )
              )
            )
      )
    )
  );

  resetApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromSettingActions.resetApplication),
      concatMap(async () => {
        const id = v4();
        this.loadingService.setLoading(id, true);

        let isAlternateIconUsed = false;
        if (Capacitor.isNativePlatform() && this.platform.is('ios')) {
          try {
            isAlternateIconUsed = !isNull(await AppIcon.getName());
          } catch {
            this.loggerService.handleError(new Error('Capacitor App Icon plugin failure (getName)'));
          }
        }
        if (Capacitor.isNativePlatform() && isAlternateIconUsed) {
          try {
            await AppIcon.reset({});
          } catch {
            this.loggerService.handleError(new Error('Capacitor App Icon plugin failure (reset)'));
          }
        }
        setTimeout(async () => {
          window.location.reload();
        }, 2000);
        return fromPersistedStateActions.persistedStateReset();
      })
    )
  );

  refreshReferralCodeFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromSettingActions.refreshReferralCodeFailure),
        concatMap(async ({ payload: { error } }) => {
          const alert = await this.alertController.create({
            backdropDismiss: false,
            buttons: [
              {
                role: 'cancel',
                text: 'Ok'
              }
            ],
            header: 'Oops',
            message: error.message
          });
          await alert.present();
          await alert.onDidDismiss();
        })
      ),
    {
      dispatch: false
    }
  );
}
